import React from 'react';
import { shallowEqual, useSelector } from 'react-redux';

import { ChatSettingsProviderProps } from '@common/react/components/Chat/ChatSettingsProvider';
import ChatSettingsProviderWithMorePlugins from '@common/react/components/Chat/ChatSettingsProviderWithMorePlugins';
import { getChatName } from '@common/react/components/Chat/ChatUtils';
import { ChatPlugins } from '@common/react/components/Chat/Chat';
import { VideoChatPlugin } from '@common/react/components/Chat/VideoChat/VideoChatPlugin';
import { VoiceCallPlugin } from '@common/react/components/Chat/VoiceCallPlugin/VoiceCallPlugin';
import { SearchingPlugin } from '@common/react/components/Chat/SearchingPlugin/SearchingPlugin';
import { ArchivePlugin } from '@common/react/components/Chat/ArchivePlugin/ArchivePlugin';
import ChatNotificationHandler from '@common/react/components/Chat/ChatNotificationHandler';

import { Feature } from '@commonTuna/react/objects/CompanyFeature';

import { ApplicationState } from '@app/store';
import { useCompanyFeatureProviderContext } from '@app/components/UI/CompanyFeaturesSetter/CompanyFeaturesSetter';

interface Props extends ChatSettingsProviderProps {
	render?: () => React.ReactNode;
}

const SimpleChatSettingsProvider: React.FC<Props> = (props) => {
	const user = useSelector((state: ApplicationState) => state.login.user, shallowEqual);
	const { checkFeatureAccess } = useCompanyFeatureProviderContext();
	const messagesHeaderComponents = props.messagesHeaderComponents
		?.filter((item) => (item !== ChatPlugins.VoiceCall && item !== ChatPlugins.VideoChat) || checkFeatureAccess(Feature.VideoChat));

	return user ? (
		<ChatSettingsProviderWithMorePlugins
			chatFormButtonsWrappers={{
				[ChatPlugins.VideoMessage]: (children) => checkFeatureAccess(Feature.VideoMessage) && children,
			}}
			renderChatName={({ currentChat, user }) => {
				const name = getChatName(currentChat, user.id);
				return <span title={Array.isArray(name) ? name.join(' ') : name}>{name}</span>;
			}}
			{...props}
			plugins={{
				[ChatPlugins.VideoChat]: VideoChatPlugin,
				[ChatPlugins.VoiceCall]: VoiceCallPlugin,
				[ChatPlugins.Searching]: SearchingPlugin,
				[ChatPlugins.Archive]: ArchivePlugin,
				...props.plugins,
			} as any}
			chatListHeaderSettings={props.chatListHeaderSettings}
			messagesHeaderComponents={messagesHeaderComponents}
			showMessagesButtonInVideoModal
			title=""
		>
			{props.children}
			<ChatNotificationHandler />
		</ChatSettingsProviderWithMorePlugins>
	) : <>{props.render ? props.render() : null}</>;
};

export default SimpleChatSettingsProvider;
